import { useState } from "react";
import { helpUrl } from "../lib/helpLinks";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Banner,
  BrandMark,
  Button,
  Checkbox,
  OnboardingStepList,
  ProgressBar,
  Card,
  Spinner,
  type Step,
} from "@guestpilot/ui-kit";
import { Copy16Regular } from "@fluentui/react-icons";
import { useGet, useWrite } from "../api/hooks";
import type { Schema } from "../api/types";
import { ENV } from "../env";
import { useHandleApiError } from "../errors";
import { formatDate } from "../format";
import { LegalDocDialog, type LegalDoc } from "../components/LegalDocDialog";
import { useI18n } from "../i18n";
import { useSession } from "../session";
import { authenticateInTeams } from "../teams";

type Status = Schema<"EnrollmentStatus">;
type ConsentUrl = { consentUrl?: string; forwardableMessage?: string };
type Progress = Schema<"OnboardingProgress">;

const DOCS: readonly LegalDoc[] = ["tos", "privacy", "dpa", "retention", "portability"];

/** Legal acceptance comes first, then admin consent; a person who cannot consent forwards a link (BR-005, BR-150, BR-151, BR-180). */
export function Enrollment() {
  const { t } = useI18n();
  const nav = useNavigate();
  const session = useSession();
  const fail = useHandleApiError();
  const status = useGet<Status>("/api/v1/enrollment/status");
  const accept = useWrite<unknown, Schema<"LegalAcceptance">>("POST", "/api/v1/enrollment/legal-acceptance", [
    "/api/v1/enrollment",
    "/api/v1/me",
  ]);
  const [accepted, setAccepted] = useState(false);
  const [reading, setReading] = useState<LegalDoc | null>(null);
  const [forward, setForward] = useState(false);
  const consent = useGet<ConsentUrl>("/api/v1/enrollment/consent-url", undefined, {
    enabled: status.data?.legalAccepted === true,
  });
  const [busy, setBusy] = useState(false);
  const [consentFailed, setConsentFailed] = useState(false);

  if (status.isLoading)
    return (
      <Centered>
        <Spinner label={t("common:loading")} />
      </Centered>
    );
  const legal = status.data?.currentLegal ?? {};

  const grant = async () => {
    if (!consent.data?.consentUrl) return;
    setBusy(true);
    setConsentFailed(false);
    try {
      // the signed link is valid for an hour: a page that stayed open longer asks for a new one at the click
      const fresh = await consent.refetch();
      const url = fresh.data?.consentUrl ?? consent.data.consentUrl;
      // The consent is complete when the API has activated the tenant, whatever happens to the popup: Microsoft's pages can sever
      // the link between Teams and the popup (Cross-Origin-Opener-Policy), so the popup may never report back. The page therefore
      // also asks the API every two seconds, and takes whichever comes first.
      let stop = false;
      const activated = async (): Promise<boolean> => {
        for (let i = 0; i < 150 && !stop; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          const s = await status.refetch();
          if (s.data && s.data.tenantState !== "pending_consent") return true;
        }
        return false;
      };
      const popup = authenticateInTeams(url).then(
        () => true,
        async () => {
          // closed or refused: one last look, the consent may have gone through just before
          await new Promise((r) => setTimeout(r, 2500));
          const s = await status.refetch();
          return Boolean(s.data && s.data.tenantState !== "pending_consent");
        },
      );
      const ok = await Promise.race([activated(), popup]);
      stop = true;
      if (!ok) throw new Error("consent not completed");
      await session.refresh();
    } catch {
      // the consent window could not open, or was closed or refused: say so, and the button can be used again
      setConsentFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Centered>
      <BrandMark size={64} />
      <h1 className="t-title-lg">{t("ui:enroll.welcome", { name: session.me.person.displayName })}</h1>
      <p className="t-body gp-muted">{t("ui:enroll.subhead")}</p>

      {!status.data?.legalAccepted ? (
        <Card title={t("ui:enroll.legalTitle")}>
          <p className="t-body gp-muted">{t("ui:enroll.legalBody")}</p>
          <ul
            className="gp-stack"
            style={{ listStyle: "none", margin: 0, padding: 0 }}
            aria-label={t("ui:enroll.legalTitle")}
          >
            {DOCS.map((d) => (
              <li key={d} className="gp-row">
                <Button
                  variant="ghost"
                  onClick={() => setReading(d)}
                  aria-label={t("ui:enroll.readDoc", { doc: t(`ui:enroll.doc.${d}`) })}
                >
                  {t(`ui:enroll.doc.${d}`)}
                </Button>
                <span className="t-body-sm gp-faint">{legal[d] ?? ""}</span>
              </li>
            ))}
          </ul>
          <Checkbox checked={accepted} onChange={setAccepted} label={t("ui:enroll.acceptOne")} />
          <div className="gp-actions">
            <Button
              variant="primary"
              disabledReason={accepted ? undefined : t("ui:enroll.acceptAll")}
              onClick={() =>
                accept.mutate(
                  {
                    body: {
                      tosVersion: legal["tos"] ?? "",
                      privacyVersion: legal["privacy"] ?? "",
                      dpaVersion: legal["dpa"] ?? "",
                      retentionVersion: legal["retention"] ?? "",
                      ...(legal["portability"] ? { portabilityVersion: legal["portability"] } : {}),
                    },
                  },
                  { onError: fail },
                )
              }
            >
              {t("ui:enroll.accept")}
            </Button>
          </div>
          <LegalDocDialog
            doc={reading}
            title={reading ? t(`ui:enroll.doc.${reading}`) : ""}
            {...(reading && legal[reading] ? { version: legal[reading] } : {})}
            onClose={() => setReading(null)}
          />
        </Card>
      ) : (
        <Card title={t("ui:enroll.consentTitle")}>
          <p className="t-body gp-muted">{t("ui:enroll.consentBody")}</p>
          <ul className="t-body gp-muted" style={{ margin: 0 }}>
            <li>{t("ui:enroll.perm.users")}</li>
            <li>{t("ui:enroll.perm.teams")}</li>
            <li>{t("ui:enroll.perm.noContent")}</li>
          </ul>
          <div className="gp-actions">
            <Button
              variant="primary"
              onClick={() => void grant()}
              disabled={!consent.data?.consentUrl || busy}
            >
              {busy ? t("common:loading") : t("ui:enroll.grant")}
            </Button>
            <Button variant="ghost" onClick={() => setForward(true)}>
              {t("ui:enroll.notAdmin")}
            </Button>
          </div>
          {consentFailed ? (
            <p className="t-body" role="alert">
              {t("ui:enroll.consentFailed")}
            </p>
          ) : null}
          {forward && consent.data ? (
            <div className="gp-stack">
              <p className="t-body-strong">{t("ui:enroll.forwardTitle")}</p>
              <pre
                className="t-body-sm"
                style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", maxWidth: "100%", margin: 0 }}
                data-testid="forwardable"
              >
                {consent.data.forwardableMessage}
              </pre>
              <div className="gp-actions">
                <Button
                  onClick={() => void navigator.clipboard?.writeText(consent.data?.forwardableMessage ?? "")}
                >
                  <Copy16Regular aria-hidden /> {t("ui:enroll.copy")}
                </Button>
                <Button variant="ghost" onClick={() => nav("/consent-help")}>
                  {t("ui:enroll.help")}
                </Button>
              </div>
            </div>
          ) : null}
        </Card>
      )}
      {ENV.mock ? <Banner tone="neutral">{t("ui:enroll.mockNote")}</Banner> : null}
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="gp-state-page">
      <div className="gp-page gp-page--narrow" style={{ width: "100%", alignItems: "stretch" }}>
        {children}
      </div>
    </div>
  );
}

export { Centered };

/** First-run checklist (onboarding.md): five steps, then straight to Home once complete. */
export function Onboarding() {
  const { t, locale } = useI18n();
  const session = useSession();
  const nav = useNavigate();
  const q = useGet<Progress>("/api/v1/onboarding");
  const sub = useGet<Schema<"Subscription">>("/api/v1/subscription", undefined, {
    enabled: session.can("SUBSCRIPTION_READ"),
  });
  if (q.isLoading)
    return (
      <Centered>
        <Spinner label={t("common:loading")} />
      </Centered>
    );
  const progress = q.data;
  const done = new Set<string>(
    (progress?.steps ?? []).filter((s) => s.done).flatMap((s) => (s.key ? [s.key] : [])),
  );
  if (progress && (progress.completed ?? 0) >= (progress.total ?? 5)) return <Navigate to="/" replace />;

  const required = (progress?.steps ?? []).filter((s) => !s.optional);
  const next = required.find((s) => !s.done)?.key;
  const stateOf = (key: string): Step["state"] => (done.has(key) ? "done" : key === next ? "next" : "later");
  const steps: Step[] = [
    {
      key: "signed_in",
      title: t("ui:onboarding.signedIn"),
      detail: session.me.person.email
        ? t("ui:onboarding.signedInAs", { email: session.me.person.email })
        : undefined,
      state: "done",
    },
    {
      key: "trial_started",
      title: t("ui:onboarding.trial"),
      detail: sub.data
        ? t("ui:onboarding.trialDetail", {
            limit: sub.data.effectiveTeamLimit ?? 0,
            date: formatDate(sub.data.trialEndsAt, locale, session.me.tenant.timeZone),
          })
        : undefined,
      state: stateOf("trial_started"),
    },
    {
      key: "first_team",
      title: t("ui:onboarding.firstTeam"),
      state: stateOf("first_team"),
      actionLabel: t("ui:onboarding.activate"),
      onAction: () => nav("/teams?activate=1"),
    },
    {
      key: "first_scan",
      title: t("ui:onboarding.firstScan"),
      state: stateOf("first_scan"),
      actionLabel: t("ui:onboarding.scan"),
      laterLabel: t("ui:onboarding.notYet"),
      onAction: () => nav("/teams"),
    },
    {
      key: "first_campaign",
      title: t("ui:onboarding.firstCampaign"),
      state: stateOf("first_campaign"),
      actionLabel: t("ui:onboarding.launch"),
      laterLabel: t("ui:onboarding.notYet"),
      onAction: () => nav("/campaigns?new=1"),
    },
  ];
  return (
    <Centered>
      <BrandMark size={64} />
      <h1 className="t-title-lg">{t("ui:onboarding.welcome", { name: session.me.person.displayName })}</h1>
      <p className="t-body gp-muted">{t("ui:onboarding.subhead")}</p>
      <section className="gp-panel" aria-label={t("ui:onboarding.steps")}>
        <OnboardingStepList steps={steps} />
      </section>
      <ProgressBar
        value={progress?.completed ?? 0}
        max={progress?.total ?? 5}
        label={t("ui:onboarding.progress", { n: progress?.completed ?? 0, total: progress?.total ?? 5 })}
      />
      <p className="t-body-sm gp-muted">
        {t("ui:onboarding.progress", { n: progress?.completed ?? 0, total: progress?.total ?? 5 })} ·{" "}
        {t("ui:onboarding.later")}
      </p>
      <Button variant="ghost" onClick={() => nav("/")}>
        {t("ui:onboarding.skip")}
      </Button>
    </Centered>
  );
}

/** Page behind the forwardable link for a person who cannot grant consent: what is asked and why, in plain language (BR-151, BR-268). */
export function ConsentHelp() {
  const { t, locale } = useI18n();
  return (
    <Centered>
      <h1 className="t-title-lg">{t("ui:consentHelp.title")}</h1>
      <p className="t-body-lg gp-muted">{t("ui:consentHelp.lead")}</p>
      <Card title={t("ui:consentHelp.whatTitle")}>
        <ul className="t-body" style={{ margin: 0 }}>
          <li>{t("ui:consentHelp.read")}</li>
          <li>{t("ui:consentHelp.teams")}</li>
          <li>{t("ui:consentHelp.never")}</li>
        </ul>
      </Card>
      <Card title={t("ui:consentHelp.whoTitle")}>
        <p className="t-body" style={{ margin: 0 }}>
          {t("ui:consentHelp.who")}
        </p>
      </Card>
      <Card title={t("ui:consentHelp.removalTitle")}>
        <p className="t-body" style={{ margin: 0 }}>
          {t("ui:consentHelp.removal")}
        </p>
      </Card>
      {helpUrl("permissions", locale) ? (
        <a
          className="t-body-strong"
          href={helpUrl("permissions", locale) ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("ui:consentHelp.more")}
        </a>
      ) : null}
    </Centered>
  );
}
